import { type ReactNode } from 'react';
import { Link as RouterLink, Outlet, useLocation } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';

import theme from './theme';
import ColorModeToggle from './components/ColorModeToggle';
import { ErrorBoundary } from './components/ErrorBoundary';
import { ErrorSnackbarProvider } from './components/ErrorSnackbar';
import AuditRunStatusBanner from './components/AuditRunStatusBanner';
import UserMenu from './components/UserMenu';

interface AppShellProps {
  children?: ReactNode;
}

const NAV_ITEMS = [
  { label: 'Clients', to: '/clients' },
  { label: 'Dashboard', to: '/dashboard' },
  { label: 'Alerts', to: '/alerts' },
  { label: 'Settings', to: '/settings' },
] as const;

/**
 * AppShell — top-level layout wrapping all routes.
 * NSG-branded navigation with black/red/white palette.
 */
export default function AppShell({ children }: AppShellProps) {
  const location = useLocation();

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AppBar
        position="sticky"
        sx={(t) => ({
          bgcolor: 'rgba(10, 10, 10, 0.85)',
          borderBottom: `1px solid ${t.palette.divider}`,
          ...t.applyStyles('light', {
            bgcolor: 'rgba(250, 250, 250, 0.85)',
          }),
        })}
      >
        <Toolbar sx={{ gap: 1 }}>
          {/* Brand */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mr: 3 }}>
            <Box component="img" src="/nsg-icon.png" alt="NSG" sx={{ height: 28 }} />
            <Typography
              variant="h6"
              component="div"
              sx={(t) => ({
                fontWeight: 700,
                letterSpacing: '-0.02em',
                fontSize: '1.05rem',
                color: 'text.primary',
                ...t.applyStyles('light', {
                  color: 'grey.900',
                }),
              })}
            >
              Agent Audit
            </Typography>
          </Box>

          {/* Navigation */}
          <Box sx={{ display: 'flex', gap: 0.5, flexGrow: 1 }}>
            {NAV_ITEMS.map(({ label, to }) => {
              const isActive = location.pathname.startsWith(to);
              return (
                <Button
                  key={to}
                  component={RouterLink}
                  to={to}
                  size="small"
                  sx={(t) => ({
                    color: isActive ? 'primary.main' : 'text.secondary',
                    bgcolor: isActive ? 'action.selected' : 'transparent',
                    borderRadius: 2,
                    px: 2,
                    fontWeight: isActive ? 700 : 500,
                    fontSize: '0.8125rem',
                    '&:hover': {
                      bgcolor: isActive ? 'action.selected' : 'action.hover',
                    },
                    ...t.applyStyles('light', {
                      color: isActive ? 'primary.dark' : 'text.secondary',
                    }),
                  })}
                >
                  {label}
                </Button>
              );
            })}
          </Box>

          <ColorModeToggle />
          <UserMenu />
        </Toolbar>
      </AppBar>
      <ErrorSnackbarProvider>
        <Box
          component="main"
          sx={{
            px: { xs: 2, sm: 3, md: 4 },
            py: 3,
            maxWidth: 1440,
            mx: 'auto',
          }}
        >
          <AuditRunStatusBanner />
          <ErrorBoundary>{children ?? <Outlet />}</ErrorBoundary>
        </Box>
      </ErrorSnackbarProvider>
    </ThemeProvider>
  );
}
